import React, { useRef, useEffect, useState } from 'react';
import GraphNode from './GraphNode';
import GraphEdge from './GraphEdge';
import { calculateLayout, NODE_WIDTH, NODE_HEIGHT } from './GraphLayout';
import './GraphCanvas.css';

/**
 * GraphCanvas Component
 * Main canvas container for the graph visualization
 */
function GraphCanvas({ nodes, edges, activeEdgeId, activeNodeId, onNodeClick, onEdgeClick }) {
  const canvasRef = useRef(null);
  const [nodePositions, setNodePositions] = useState(new Map());
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  
  // Calculate layout when nodes or edges change
  useEffect(() => {
    if (nodes.length > 0) {
      const positions = calculateLayout(nodes, edges);
      setNodePositions(positions);
      
      // Calculate canvas dimensions
      let maxX = 0;
      let maxY = 0;
      positions.forEach(({ x, y }) => {
        maxX = Math.max(maxX, x);
        maxY = Math.max(maxY, y);
      });
      
      setDimensions({
        width: maxX + 300,
        height: maxY + 200
      });
    }
  }, [nodes, edges]);
  
  // Handle canvas click (for edge selection)
  const handleCanvasClick = (e) => {
    if (e.target === canvasRef.current || e.target.tagName === 'svg') {
      // Clicked on canvas background
      if (onEdgeClick) {
        onEdgeClick(null);
      }
    }
  };
  
  return (
    <div className="graph-canvas-container" onClick={handleCanvasClick}>
      <svg
        ref={canvasRef}
        className="graph-canvas-svg"
        width={dimensions.width}
        height={dimensions.height}
        viewBox={`0 0 ${dimensions.width} ${dimensions.height}`}
      >
        {/* Render edges first (so they appear behind nodes) */}
        {edges.map(edge => {
          const fromPos = nodePositions.get(edge.from);
          const toPos = nodePositions.get(edge.to);
          
          if (!fromPos || !toPos) return null;
          
          return (
            <GraphEdge
              key={edge.id}
              edge={edge}
              fromPos={fromPos}
              toPos={toPos}
              isHighlighted={activeEdgeId === edge.id}
              nodeWidth={NODE_WIDTH}
              nodeHeight={NODE_HEIGHT}
            />
          );
        })}
      </svg>
      
      {/* Render nodes (DOM-based) */}
      <div className="graph-nodes-container">
        {nodes.map(node => {
          const position = nodePositions.get(node.id);
          if (!position) return null;
          
          return (
            <GraphNode
              key={node.id}
              node={node}
              position={position}
              isActive={activeNodeId === node.id || edges.some(e => 
                (e.from === node.id || e.to === node.id) && e.id === activeEdgeId
              )}
              isProcessing={activeNodeId === node.id}
              onClick={onNodeClick}
            />
          );
        })}
      </div>
    </div>
  );
}

export default GraphCanvas;

